import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { Toaster } from "sonner";

import { ThemeProvider } from "@/context/ThemeContext";
import { AuthProvider } from "@/context/AuthContext";
import { BrandingProvider } from "@/context/BrandingContext";

import AppLayout from "@/components/layout/AppLayout";
import RequireAuth from "@/components/layout/RequireAuth";
import RootRedirect from "@/components/RootRedirect";
import MarketingRedirect from "@/components/MarketingRedirect";
import NotFound from "@/pages/NotFound";

// Keep Forge Vision share pages on the app subdomain (real app content,
// rendered from backend on a signed slug — NOT marketing).
import { ForgeVisionShare } from "@/pages/tools/ForgeVisionShare";

import Login from "@/pages/app/Login";
import Register from "@/pages/app/Register";
import VerifyEmail from "@/pages/app/VerifyEmail";
import ForgotPassword from "@/pages/app/ForgotPassword";
import AcceptInvite from "@/pages/app/AcceptInvite";
import Onboarding from "@/pages/app/Onboarding";
import Dashboard from "@/pages/app/Dashboard";
import PlaceholderPage from "@/pages/app/PlaceholderPage";
import BrandingSettings from "@/pages/app/BrandingSettings";
import UsersSettings from "@/pages/app/UsersSettings";
import SubscriptionSettings from "@/pages/app/SubscriptionSettings";
import IntegrationsSettings from "@/pages/app/IntegrationsSettings";
import SoftwareBrandingSettings from "@/pages/app/SoftwareBrandingSettings";
import ProfileSettings from "@/pages/app/ProfileSettings";
import LeadInbox from "@/pages/software/LeadInbox";

import CRMBoard from "@/pages/app/crm/CRMBoard";
import Contacts from "@/pages/app/crm/Contacts";
import ContactDetail from "@/pages/app/crm/ContactDetail";
import PropertiesList, { PropertyNew } from "@/pages/app/properties/PropertiesList";
import PropertyDetail from "@/pages/app/properties/PropertyDetail";
import DealsList from "@/pages/app/deals/DealsList";
import DealNew from "@/pages/app/deals/DealNew";
import DealDetail from "@/pages/app/deals/DealDetail";
import ProjectsList from "@/pages/app/projects/ProjectsList";
import ProjectWorkspace from "@/pages/app/projects/ProjectWorkspace";
import JurisdictionsAdmin from "@/pages/app/admin/JurisdictionsAdmin";
import { InvestorsList, LendersList } from "@/pages/app/business/Business";
import PortalUsersAdmin from "@/pages/app/PortalUsersAdmin";
import DemoDataAdmin from "@/pages/app/DemoDataAdmin";
import InvestorUpdatesAdmin from "@/pages/app/InvestorUpdatesAdmin";
import NotificationsPage from "@/pages/app/NotificationsPage";

// Portal (stakeholder) pages
import PortalLayout from "@/pages/portal/PortalLayout";
import PortalLogin from "@/pages/portal/PortalLogin";
import PortalRedeem from "@/pages/portal/PortalRedeem";
import PortalProjectsList from "@/pages/portal/PortalProjectsList";
import PortalProjectDetail from "@/pages/portal/PortalProjectDetail";
import PublicInvestorUpdate from "@/pages/public/PublicInvestorUpdate";

// Embeds (iframe-friendly)
import ForgeVisionEmbed from "@/pages/embed/ForgeVisionEmbed";
import DealAnalyzerEmbed from "@/pages/embed/DealAnalyzerEmbed";
import { initGA4 } from "@/lib/analytics";

function App() {
    React.useEffect(() => { initGA4(); }, []);

    return (
        <BrowserRouter>
            <ThemeProvider>
                <AuthProvider>
                    <BrandingProvider>
                        <Toaster position="top-right" richColors closeButton />
                        <Routes>
                            {/* Root — auth-aware */}
                            <Route path="/" element={<RootRedirect />} />

                            {/* Shareable result URLs — STAY on app subdomain */}
                            <Route path="/forge-vision/r/:slug" element={<ForgeVisionShare />} />
                            <Route path="/deal-intelligence/r/:slug" element={<ForgeVisionShare />} />

                            {/* Auth pages */}
                            <Route path="/login" element={<Login />} />
                            <Route path="/register" element={<Register />} />
                            <Route path="/verify-email" element={<VerifyEmail />} />
                            <Route path="/forgot-password" element={<ForgotPassword />} />
                            <Route path="/reset-password" element={<ForgotPassword />} />
                            <Route path="/accept-invite" element={<AcceptInvite />} />
                            <Route path="/app/onboarding" element={<RequireAuth><Onboarding /></RequireAuth>} />

                            {/* /app/* auth aliases */}
                            <Route path="/app/login" element={<Navigate to="/login" replace />} />
                            <Route path="/app/register" element={<Navigate to="/register" replace />} />
                            <Route path="/app/verify-email" element={<Navigate to="/verify-email" replace />} />
                            <Route path="/app/forgot-password" element={<Navigate to="/forgot-password" replace />} />
                            <Route path="/app/accept-invite" element={<Navigate to="/accept-invite" replace />} />

                            {/* Authenticated app */}
                            <Route path="/app" element={<RequireAuth><AppLayout /></RequireAuth>}>
                                <Route index element={<Navigate to="/app/dashboard" replace />} />
                                <Route path="dashboard" element={<Dashboard />} />
                                <Route path="deals" element={<DealsList />} />
                                <Route path="deals/new" element={<DealNew />} />
                                <Route path="deals/:id" element={<DealDetail />} />
                                <Route path="properties" element={<PropertiesList />} />
                                <Route path="properties/new" element={<PropertyNew />} />
                                <Route path="properties/:id" element={<PropertyDetail />} />
                                <Route path="crm" element={<CRMBoard />} />
                                <Route path="contacts" element={<Contacts />} />
                                <Route path="contacts/:id" element={<ContactDetail />} />
                                <Route path="projects" element={<ProjectsList />} />
                                <Route path="projects/:id" element={<ProjectWorkspace />} />
                                <Route path="admin/jurisdictions" element={<JurisdictionsAdmin />} />
                                <Route path="business/investors" element={<InvestorsList />} />
                                <Route path="business/lenders" element={<LendersList />} />
                                <Route path="business/updates" element={<InvestorUpdatesAdmin />} />
                                <Route path="admin/portal-users" element={<PortalUsersAdmin />} />
                                <Route path="admin/demo-data" element={<DemoDataAdmin />} />
                                <Route path="notifications" element={<NotificationsPage />} />
                                <Route path="build" element={<PlaceholderPage moduleName="Forge Build" testid="build-page" />} />
                                <Route path="docs" element={<PlaceholderPage moduleName="Forge Docs" testid="docs-page" />} />
                                <Route path="field" element={<PlaceholderPage moduleName="Forge Field" testid="field-page" />} />
                                <Route path="permits" element={<PlaceholderPage moduleName="Permits & Inspections" testid="permits-page" />} />
                                <Route path="finance" element={<PlaceholderPage moduleName="Forge Finance" testid="finance-page" />} />
                                <Route path="capital" element={<PlaceholderPage moduleName="Forge Capital" testid="capital-page" />} />
                                <Route path="ai" element={<PlaceholderPage moduleName="Forge AI" testid="ai-page" />} />
                                <Route path="reports" element={<PlaceholderPage moduleName="Forge Analytics" testid="reports-page" />} />
                                <Route path="studio" element={<PlaceholderPage moduleName="Forge Studio" testid="studio-page" />} />
                                <Route path="settings" element={<Navigate to="/app/settings/branding" replace />} />
                                <Route path="settings/branding" element={<BrandingSettings />} />
                                <Route path="settings/software-branding" element={<RequireAuth superOnly><SoftwareBrandingSettings /></RequireAuth>} />
                                <Route path="settings/users" element={<UsersSettings />} />
                                <Route path="settings/subscription" element={<SubscriptionSettings />} />
                                <Route path="settings/integrations" element={<IntegrationsSettings />} />
                                <Route path="settings/profile" element={<ProfileSettings />} />
                                <Route path="admin/leads" element={<RequireAuth superOnly><LeadInbox /></RequireAuth>} />
                                {/* Nested /app/* unknown → 404 shell, do NOT redirect to marketing */}
                                <Route path="*" element={<NotFound />} />
                            </Route>

                            {/* Portal (stakeholder) area */}
                            <Route path="/portal/login" element={<PortalLogin />} />
                            <Route path="/portal/redeem" element={<PortalRedeem />} />
                            <Route path="/portal" element={<PortalLayout />}>
                                <Route index element={<Navigate to="/portal/projects" replace />} />
                                <Route path="projects" element={<PortalProjectsList />} />
                                <Route path="projects/:id" element={<PortalProjectDetail />} />
                            </Route>

                            {/* Public signed investor update */}
                            <Route path="/public/investor-update" element={<PublicInvestorUpdate />} />

                            {/* Iframe embeds */}
                            <Route path="/embed/forge-vision" element={<ForgeVisionEmbed />} />
                            <Route path="/embed/deal-analyzer" element={<DealAnalyzerEmbed />} />
                            <Route path="/embed/deal-intelligence" element={<DealAnalyzerEmbed />} />

                            {/* Catch-all — unknown / legacy marketing path → Vercel */}
                            <Route path="*" element={<MarketingRedirect />} />
                        </Routes>
                    </BrandingProvider>
                </AuthProvider>
            </ThemeProvider>
        </BrowserRouter>
    );
}

export default App;
